import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectRoles } from 'reduxStore/roles/selector';
import { peopleActions } from 'reduxStore/people/slice';
import { useManagePermittedRoles } from 'library/hooks';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';
import { rolesConfig } from 'config/rolesConfig';

const { Title, Text } = Typography;

const DeletePeopleModal = ({ children, personIDs = [], visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// Permission checks
	const canDeletePerson = useManagePermittedRoles('deletePerson');
	const isGlobalAdmin = useManagePermittedRoles('revokeRole');
	const roles = useSelector(selectRoles);
	const rolesLoaded = useSelector((state) => state.Roles.rolesLoadingComplete);
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	// ✅ Local State
	const [internalVisible, internalSetVisible] = useState(false);

	// ✅ If visible is not provided, use internal state
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : internalSetVisible;

	// ✅ Get all people from store
	const people = useSelector(selectPeople);

	// ✅ Get loading and success states
	const { loadingDeleteProfile, deletePersonComplete } = useSelector((state) => state.People);

	// ✅ Filter selected people
	const filteredPeople = useMemo(
		() => people.filter((person) => personIDs.includes(person.personID)),
		[people, personIDs]
	);

	// ✅ Generate selected people names
	const selectedNames = useMemo(
		() =>
			filteredPeople.map((person) =>
				person.firstName && person.lastName
					? `${person.firstName} ${person.lastName}`
					: person.displayName || person.email || '-'
			),
		[filteredPeople]
	);

	// ✅ Check if any selected person has an admin role (site admin can't delete admins)
	const hasAdminRole = useMemo(() => {
		const adminIDs = [
			rolesConfig.GLOBAL_ADMIN,
			rolesConfig.SITE_ADMIN,
			rolesConfig.GLOBAL_READ_ONLY,
			rolesConfig.SITE_READ_ONLY
		];
		return personIDs.some((pID) => roles.some((r) => r.personID === pID && adminIDs.includes(r.permissionID)));
	}, [personIDs, roles]);

	// Disable delete if user doesn't have permission, roles haven't loaded, or trying to delete an admin as site admin
	const isDisabled = !canDeletePerson || (!isGlobalAdmin && (!rolesLoaded || hasAdminRole));

	// ✅ Generate dynamic disabled message
	const disabledMessage = !canDeletePerson
		? permissionDeniedMessage
		: hasAdminRole
			? permissionDeniedMessage
			: "Delete this Person's Profile";

	// ✅ Generate dynamic messages
	const generateDeleteTitle = () =>
		personIDs.length > 1
			? `Are you sure you want to delete these ${personIDs.length} profiles?`
			: `Are you sure you want to delete this profile?`;

	const generateDeleteButtonText = () => (personIDs.length > 1 ? 'Delete Profiles' : 'Delete');

	// ✅ Close modal
	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// ✅ Handle deletion
	const handleDelete = () => dispatch(peopleActions.deletePerson({ personIDs }));

	// ✅ Close modal on successful deletion
	useEffect(() => {
		if (deletePersonComplete) handleCancel();
	}, [deletePersonComplete, handleCancel]);

	return (
		<>
			<Tooltip title={disabledMessage} placement='top'>
				<span onClick={() => !isDisabled && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<DeletedFilled color='currentColor' width={16} height={16} />}
							disabled={isDisabled}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingDeleteProfile} block>
							Cancel
						</Button>
						<Button
							onClick={handleDelete}
							loading={loadingDeleteProfile}
							disabled={loadingDeleteProfile}
							type='primary'
							danger
							block
						>
							{generateDeleteButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{generateDeleteTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action is permanent and <br /> cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected People</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DeletePeopleModal;
